import { useEffect, useRef, useState } from "react";
import { layoutVerdict, LayoutVerdict } from "../../layoutVerdict";
import { restoreStats } from "../ScrollRestoration";

export const DEBUG_KEY = "dss.debug";

export const isDebugOn = () => {
  try {
    return localStorage.getItem(DEBUG_KEY) === "1";
  } catch {
    return false;
  }
};

export const toggleDebug = () => {
  try {
    localStorage.setItem(DEBUG_KEY, isDebugOn() ? "0" : "1");
  } catch {
    /* private mode — nothing to do */
  }
};

type Readings = {
  verdict: LayoutVerdict;
  standalone: string;
  view: string;
  viewportMeta: string;
  cssHeights: string;
  safeTop: string;
  safeBottom: string;
  appHeight: string;
  restore: string;
  bar: string;
  gap: string;
  scroll: string;
  doc: string;
  lowest: string;
};

const CSS_UNITS = ["dvh", "svh", "lvh"] as const;

const read = (lowestSoFar: number, probes: Record<string, HTMLElement | null>): Readings => {
  const root = document.documentElement;
  const bar = document.querySelector<HTMLElement>(".dss-tab-bar");
  const scroller = document.querySelector<HTMLElement>("[data-scroll-container]");
  const barBox = bar?.getBoundingClientRect();
  const shell = document.querySelector<HTMLElement>(".dss-app-shell");
  return {
    // The whole readout in one word. Everything below it is the working, for
    // when it says something is wrong and the question becomes what.
    verdict: layoutVerdict({
      standalone: root.hasAttribute("data-standalone"),
      shellHeight: shell?.getBoundingClientRect().height ?? 0,
      screenHeight: window.screen.height,
      scrollY: window.scrollY,
      barPaddingBottom: bar ? parseFloat(getComputedStyle(bar).paddingBottom) : 0,
      safeBottom: parseFloat(getComputedStyle(root).getPropertyValue("--safe-bottom")) || 0,
    }),
    standalone: root.hasAttribute("data-standalone") ? "ja" : "nej",
    // The web view against the screen it sits in. They should match. When they
    // don't, iOS is painting the difference itself, in the manifest's
    // background colour — which is the same paper as the app, so it looks like
    // the app has a margin it can neither see nor remove.
    view: `${window.innerWidth}x${window.innerHeight} af ${window.screen.width}x${window.screen.height}`,
    // Whether the *served* page actually asks for edge-to-edge layout. This
    // is read straight off the DOM, so it's true regardless of what any icon
    // cached — it only rules out (or confirms) that the wrong HTML somehow
    // reached the phone. If this says "cover" and VIEW is still short of the
    // screen, the served page is fine and the icon's launch configuration —
    // decided once, when it was added to the home screen — is the remaining
    // suspect.
    viewportMeta:
      document.querySelector('meta[name="viewport"]')?.getAttribute("content") ?? "mangler",
    // window.innerHeight and CSS viewport units are computed by different
    // code paths in the engine. Every fix so far has assumed the JS number
    // (812 of an 874px screen, on a device that shows it never moving even
    // across a freshly recreated icon) was a stale reading of the real
    // viewport — but if the CSS units disagree with it, the JS side is the
    // one that's wrong, not the underlying viewport, and everything built on
    // window.innerHeight / visualViewport so far has been measuring the
    // wrong thing correctly rather than the right thing.
    cssHeights: CSS_UNITS.map(
      (unit) => `${unit[0]}${Math.round(probes[unit]?.getBoundingClientRect().height ?? 0)}`,
    ).join(" "),
    safeTop: getComputedStyle(root).getPropertyValue("--safe-top").trim() || "?",
    safeBottom: getComputedStyle(root).getPropertyValue("--safe-bottom").trim() || "?",
    appHeight: getComputedStyle(root).getPropertyValue("--app-height").trim() || "?",
    // Runs / writes / the offset last written. If a page won't scroll up
    // and the write count keeps climbing, this component is the one
    // putting it back.
    restore: `${restoreStats.runs}k ${restoreStats.writes}w @${restoreStats.target}`,
    bar: bar ? `${Math.round(barBox!.height)} pb ${getComputedStyle(bar).paddingBottom}` : "—",
    // Two numbers, because the bar sitting right in the viewport and the bar
    // sitting right on the screen turned out to be different questions.
    //
    // The first is how much of the *viewport* is left below the tab bar —
    // documentElement.clientHeight, the box the page is laid out in and the
    // one the shell is pinned to. Should be 0. Positive means the shell is
    // coming up short of the viewport; negative means the bar hangs past the
    // bottom of it, where it is cut off.
    //
    // The second is how much of the *screen* the viewport doesn't cover at
    // all. Should also be 0. Anything else is screen the app can never paint
    // into, whatever it lays out — it reads as a band of dead space under the
    // tab bar, in the canvas colour, and it is invisible to every reading
    // above because every one of them is measured inside the viewport. That
    // band is what black-translucent was leaving at the bottom: 62 of 874.
    gap: barBox
      ? `${Math.round(root.clientHeight - barBox.bottom)}/${root.clientHeight} skærm ${
          window.screen.height - root.clientHeight
        }`
      : "—",
    // The document's own scroll: where it is, and how far it *can* go. It
    // should not be able to go anywhere — every screen inside the shell
    // scrolls its own container. Anything but 0 here means the shell is
    // taller than the viewport the engine believes in, which is exactly the
    // slack the tab bar can ride off the bottom of the screen on.
    doc: `${Math.round(window.scrollY)} af ${Math.round(
      (document.scrollingElement?.scrollHeight ?? 0) - root.clientHeight,
    )}`,
    scroll: scroller
      ? `${Math.round(scroller.scrollTop)}/${Math.round(scroller.scrollHeight - scroller.clientHeight)}`
      : "—",
    lowest: Number.isFinite(lowestSoFar) ? String(Math.round(lowestSoFar)) : "—",
  };
};

/**
 * A readout of the handful of numbers that decide how the shell lays itself
 * out, for when something looks wrong on a phone I can't measure directly.
 * Off unless switched on by tapping the version line on Mig three times.
 *
 * `lavest` is the point scrolling actually stopped at on the way back up —
 * if the page won't go above some offset, that offset shows here.
 */
const DebugOverlay = () => {
  const [on] = useState(isDebugOn);
  const [readings, setReadings] = useState<Readings | null>(null);
  const probeRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (!on) return;
    let lowest = Infinity;
    let scrolledDown = false;
    let watching: HTMLElement | null = null;
    let raf = 0;

    const tick = () => {
      const scroller = document.querySelector<HTMLElement>("[data-scroll-container]");
      // Start over whenever a different screen's scroller takes over, or the
      // reading is a mix of two unrelated pages.
      if (scroller !== watching) {
        watching = scroller;
        lowest = Infinity;
        scrolledDown = false;
      }
      if (scroller) {
        // Only start watching once they've actually gone down the page. Every
        // screen opens at 0, so a low-water mark that counts from arrival
        // always reads 0 and says nothing about how far back up they got.
        if (scroller.scrollTop > 250) scrolledDown = true;
        if (scrolledDown && scroller.scrollTop < lowest) lowest = scroller.scrollTop;
      }
      setReadings(read(lowest, probeRefs.current));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on]);

  if (!on) return null;

  return (
    <>
      {/* Invisible, unrelated to layout — exist only so their rendered height
          says what each CSS viewport unit actually resolves to on this
          device, independent of window.innerHeight / visualViewport. */}
      {CSS_UNITS.map((unit) => (
        <div
          key={unit}
          ref={(el) => {
            probeRefs.current[unit] = el;
          }}
          aria-hidden
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 0,
            height: `100${unit}`,
            visibility: "hidden",
            pointerEvents: "none",
          }}
        />
      ))}
      {readings && (
        // Above the tab bar rather than at the top, where an installed app
        // puts the status bar and notch over it. Colours inline rather than
        // as classes: the first version of this panel was invisible because
        // `bg-ink/85` never compiled, leaving pale text on pale paper. A
        // diagnostic shouldn't be able to fail that way.
        <div
          className="fixed left-2 bottom-28 z-50 rounded px-2 py-1.5 font-mono text-[9px] leading-[1.5] pointer-events-none"
          style={{ background: "rgba(20,16,12,0.9)", color: "#fff" }}
        >
          <div style={{ color: readings.verdict.ok ? "#7ddba3" : "#ff8a7a" }}>
            TJEK {readings.verdict.text}
          </div>
          <div>PWA {readings.standalone}</div>
          <div>VIEW {readings.view}</div>
          <div>COVER {readings.viewportMeta.includes("cover") ? "ja" : "NEJ"}</div>
          <div>CSS-H {readings.cssHeights}</div>
          <div>SAFE T{readings.safeTop} B{readings.safeBottom}</div>
          <div>APP-H {readings.appHeight}</div>
          <div>BAR {readings.bar}</div>
          <div>UNDER BAR {readings.gap}</div>
          <div>DOK {readings.doc}</div>
          <div>SCROLL {readings.scroll}</div>
          <div>OP TIL {readings.lowest}</div>
          <div>RESTORE {readings.restore}</div>
        </div>
      )}
    </>
  );
};

export default DebugOverlay;
